import React, { useState } from "react";
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Text,
  Textarea,
  VStack,
  useColorMode,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { PublicNavbar } from "./layout/PublicNavbar";
import { Footer } from "./layout/Footer";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3013";

export const Contact: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [message, setMessage] = useState("");
  const { t } = useTranslation();
  const { colorMode } = useColorMode();
  const toast = useToast();
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const inputBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API_URL}/contact`, {
        name,
        email,
        company,
        message,
      });
      toast({
        title: t("common.contact.form.success"),
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      setName("");
      setEmail("");
      setCompany("");
      setMessage("");
    } catch (error) {
      toast({
        title: t("common.contact.form.error"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box minH="100vh" display="flex" flexDirection="column">
      <PublicNavbar />

      <Box flex="1" bg={bgColor} py={20}>
        <Container maxW="container.md" mt={20}>
          <VStack spacing={12}>
            <VStack spacing={4} textAlign="center">
              <Heading
                size="2xl"
                fontFamily="Nasalization"
                bgGradient="linear(to-r, brand.primary, brand.secondary)"
                bgClip="text"
              >
                {t("common.contact.title")}
              </Heading>
              <Text
                fontSize="xl"
                color={useColorModeValue("gray.600", "gray.400")}
                maxW="2xl"
              >
                {t("common.contact.subtitle")}
              </Text>
            </VStack>

            <Box
              as="form"
              onSubmit={handleSubmit}
              bg={inputBg}
              p={8}
              borderRadius="xl"
              border="1px"
              borderColor={borderColor}
              shadow="md"
              w="full"
            >
              <VStack spacing={6}>
                <FormControl isRequired>
                  <FormLabel>{t("common.contact.form.name")}</FormLabel>
                  <Input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    bg={inputBg}
                    borderColor={borderColor}
                    _hover={{ borderColor: "brand.primary" }}
                    _focus={{ borderColor: "brand.primary", boxShadow: "none" }}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>{t("common.contact.form.email")}</FormLabel>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    bg={inputBg}
                    borderColor={borderColor}
                    _hover={{ borderColor: "brand.primary" }}
                    _focus={{ borderColor: "brand.primary", boxShadow: "none" }}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>{t("common.contact.form.company")}</FormLabel>
                  <Input
                    type="text"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    bg={inputBg}
                    borderColor={borderColor}
                    _hover={{ borderColor: "brand.primary" }}
                    _focus={{ borderColor: "brand.primary", boxShadow: "none" }}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>{t("common.contact.form.message")}</FormLabel>
                  <Textarea
                    rows={6}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    bg={inputBg}
                    borderColor={borderColor}
                    _hover={{ borderColor: "brand.primary" }}
                    _focus={{ borderColor: "brand.primary", boxShadow: "none" }}
                  />
                </FormControl>

                <Button
                  type="submit"
                  colorScheme="brand"
                  size="lg"
                  w="full"
                  isLoading={isSubmitting}
                  loadingText={t("common.contact.form.submit")}
                  fontFamily="Nasalization"
                >
                  {t("common.contact.form.submit")}
                </Button>
              </VStack>
            </Box>
          </VStack>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};
